// src/components/BioManager.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Importar Link
import { firebaseService } from '../services/firebaseService';
import { cld } from '../services/cloudinaryService';
import PhotoUploader from './PhotoUploader';
import { aviso } from './Avisos';
import '../styles/bioManager.css';

export default function BioManager() {
    const [fotosFavoritas, setFotosFavoritas] = useState([]);
    const [fotoPerfil, setFotoPerfil] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showUploaderFavoritas, setShowUploaderFavoritas] = useState(false);
    const [showUploaderPerfil, setShowUploaderPerfil] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Cargar datos
    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [fotosData, perfilData] = await Promise.all([
                firebaseService.getBioFotos(),
                firebaseService.getFotoPerfil()
            ]);
            
            setFotosFavoritas(fotosData);
            setFotoPerfil(perfilData);
        } catch (error) {
            console.error('Error cargando datos bio:', error);
        } finally {
            setLoading(false);
        }
    };

    // Eliminar foto de perfil
    const handleEliminarFotoPerfil = async () => {
        if (!window.confirm('¿Seguro que quieres eliminar la foto de perfil?')) {
            return;
        }

        try {
            const result = await firebaseService.deleteFotoPerfil();
            if (result.success) {
                setFotoPerfil(null);
                aviso('Foto de perfil eliminada');
            }
        } catch (error) {
            console.error('Error eliminando foto de perfil:', error);
            aviso('Error al eliminar la foto', "error");
        }
    };

    // Eliminar foto favorita
    const handleEliminarFotoFavorita = async (foto) => {
        if (!window.confirm('¿Seguro que quieres eliminar esta foto favorita?')) {
            return;
        }

        try {
            const result = await firebaseService.deleteBioPhoto(foto.id);
            if (result.success) {
                setFotosFavoritas(prev => prev.filter(f => f.id !== foto.id));
                aviso('Foto favorita eliminada');
            }
        } catch (error) {
            console.error('Error eliminando foto favorita:', error);
            aviso('Error al eliminar la foto', "error");
        }
    };

    // Mover foto favorita arriba
    const moveFotoUp = async (fotoId) => {
        const index = fotosFavoritas.findIndex(f => f.id === fotoId);
        if (index <= 0) return;
        
        const nuevasFotos = [...fotosFavoritas];
        [nuevasFotos[index], nuevasFotos[index - 1]] = [nuevasFotos[index - 1], nuevasFotos[index]];
        
        await actualizarOrdenFotos(nuevasFotos);
    };

    // Mover foto favorita abajo
    const moveFotoDown = async (fotoId) => {
        const index = fotosFavoritas.findIndex(f => f.id === fotoId);
        if (index >= fotosFavoritas.length - 1) return;
        
        const nuevasFotos = [...fotosFavoritas];
        [nuevasFotos[index], nuevasFotos[index + 1]] = [nuevasFotos[index + 1], nuevasFotos[index]];
        
        await actualizarOrdenFotos(nuevasFotos);
    };

    // Establecer como foto destacada (primera posición)
    const setAsDestacada = async (fotoId) => {
        const index = fotosFavoritas.findIndex(f => f.id === fotoId);
        if (index === 0) return;
        
        const nuevasFotos = [...fotosFavoritas];
        const [foto] = nuevasFotos.splice(index, 1);
        nuevasFotos.unshift(foto);
        
        await actualizarOrdenFotos(nuevasFotos);
    };

    // Actualizar orden en Firebase
    const actualizarOrdenFotos = async (orderedFotos) => {
        try {
            setUploading(true);
            const result = await firebaseService.updateBioPhotosOrder(orderedFotos);
            
            if (result.success) {
                setFotosFavoritas(orderedFotos);
            }
        } catch (error) {
            console.error('Error actualizando orden:', error);
            aviso('Error al actualizar el orden', "error");
        } finally {
            setUploading(false);
        }
    };

    // Guardar lo que subió PhotoUploader (si algo falla, lanza y el subidor lo avisa)
    const guardarFavoritas = async (fotos, descripcion) => {
        for (const f of fotos) {
            const r = await firebaseService.saveBioPhoto({ ...f, descripcion });
            if (!r.success) throw r.error;
        }
        // Sin cargarDatos(): su pantalla de carga desmontaría el subidor y su resumen
        setFotosFavoritas(await firebaseService.getBioFotos());
    };

    const guardarPerfil = async ([foto]) => {
        const r = await firebaseService.saveFotoPerfil(foto);
        if (!r.success) throw r.error;
        setFotoPerfil(await firebaseService.getFotoPerfil());
        setShowUploaderPerfil(false);
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Cargando gestión de Bio...</p>
            </div>
        );
    }

    return (
        <div className="bio-manager">
            <div className="manager-header">
                {/* <div className="header-top">
                    <Link to="/bio" className="back-to-bio">
                        ← Volver a Bio
                    </Link>
                </div> */}
                <h2>Gestión de la página Sobre mí</h2>
                <p>Administra tu foto de perfil y fotos favoritas</p>
            </div>

            <div className="bio-manager-content">
                {/* Sección Foto de Perfil */}
                <div className="perfil-section">
                    <div className="section-header">
                        <h3>Foto de Perfil</h3>
                        <Link to="/bio" className="btn-view-bio">
                            Ver en Sobre mí
                        </Link>
                    </div>
                    
                    <div className="perfil-container">
                        {fotoPerfil ? (
                            <div className="perfil-with-controls">
                                <img 
                                    src={cld(fotoPerfil.url, 600)} 
                                    alt="Foto de perfil" 
                                    className="perfil-img"
                                />
                                <div className="perfil-actions">
                                    <button 
                                        className="btn-cambiar-perfil"
                                        onClick={() => setShowUploaderPerfil(!showUploaderPerfil)}
                                        disabled={uploading}
                                    >
                                        Cambiar
                                    </button>
                                    <button 
                                        className="btn-eliminar-perfil"
                                        onClick={handleEliminarFotoPerfil}
                                        disabled={uploading}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="perfil-placeholder">
                                <p>No hay foto de perfil configurada</p>
                                <button 
                                    className="btn-subir-perfil"
                                    onClick={() => setShowUploaderPerfil(true)}
                                    disabled={uploading}
                                >
                                    Subir foto
                                </button>
                            </div>
                        )}
                        
                        {showUploaderPerfil && (
                            <div className="uploader-section">
                                <PhotoUploader folder="foto_perfil" onSubidas={guardarPerfil} multiple={false} />
                                <button 
                                    className="btn-cancelar"
                                    onClick={() => setShowUploaderPerfil(false)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sección Fotos Favoritas */}
                <div className="favoritas-section">
                    <div className="section-header">
                        <h3>Mis Fotos Favoritas ({fotosFavoritas.length})</h3>
                        <div className="section-controls">
                            <button 
                                className="btn-agregar-favoritas"
                                onClick={() => setShowUploaderFavoritas(!showUploaderFavoritas)}
                                disabled={uploading}
                            >
                                {showUploaderFavoritas ? 'Cancelar' : '+ Agregar Fotos'}
                            </button>
                            <Link to="/bio" className="btn-view-bio">
                                Ver en Sobre mí
                            </Link>
                        </div>
                    </div>

                    {showUploaderFavoritas && (
                        <div className="uploader-section">
                            <PhotoUploader folder="bio_fotos" onSubidas={guardarFavoritas} conDescripcion />
                            <button 
                                className="btn-cancelar"
                                onClick={() => setShowUploaderFavoritas(false)}
                            >
                                Cerrar
                            </button>
                        </div>
                    )}

                    <div className="favoritas-grid">
                        {fotosFavoritas.length === 0 ? (
                            <div className="no-fotos">
                                <p>No hay fotos favoritas todavía</p>
                            </div>
                        ) : (
                            fotosFavoritas.map((foto, index) => (
                                <div key={foto.id} className="favorita-item">
                                    <div className="favorita-image">
                                        <img 
                                            src={cld(foto.url, 400)} loading="lazy" 
                                            alt={foto.descripcion || 'Foto favorita'} 
                                        />
                                    </div>
                                    
                                    <div className="favorita-info">
                                        {foto.descripcion && (
                                            <p className="favorita-desc">{foto.descripcion}</p>
                                        )}
                                        <p className="favorita-date">
                                            {new Date(foto.fechaSubida).toLocaleDateString()}
                                        </p>
                                    </div>
                                    
                                    <div className="favorita-actions">
                                        <button
                                            className="btn-destacada"
                                            onClick={() => setAsDestacada(foto.id)}
                                            title="Poner como destacada" aria-label="Poner como destacada"
                                            disabled={index === 0 || uploading}
                                        >
                                            ★
                                        </button>
                                        
                                        <button
                                            className="btn-move-up"
                                            onClick={() => moveFotoUp(foto.id)}
                                            title="Mover arriba" aria-label="Mover arriba"
                                            disabled={index === 0 || uploading}
                                        >
                                            ↑
                                        </button>
                                        
                                        <button
                                            className="btn-move-down"
                                            onClick={() => moveFotoDown(foto.id)}
                                            title="Mover abajo" aria-label="Mover abajo"
                                            disabled={index === fotosFavoritas.length - 1 || uploading}
                                        >
                                            ↓
                                        </button>
                                        
                                        <button
                                            className="btn-eliminar"
                                            onClick={() => handleEliminarFotoFavorita(foto)}
                                            title="Eliminar foto" aria-label="Eliminar foto"
                                            disabled={uploading}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}